/* eslint-disable no-unused-vars */
import React, { useRef, useState } from 'react';
import { Panel, PanelBody, PanelHeader } from '../../../util/panel';
import { Button, Select, Table } from 'antd';
import {
  FaDownload,
  FaEye,
  FaEyeSlash,
  FaPen,
  FaPlus,
  FaUpload,
} from 'react-icons/fa';
import { MdDelete } from 'react-icons/md';
import usePaginationHook from '../../../../utils/usePagination';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { AlertConfirm, AlertError, AlertSuccess } from '../../../util/Alert';
import { FormProvider, useForm } from 'react-hook-form';
import 'dayjs/locale/th';
import { useHistory } from 'react-router';
import {
  legacySurveyQuestionFormDefaultValues,
  legacySurveyQuestionFormSchema,
} from '../components/dto/legacy-survey-form.dto';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import LegacySurveyQuestionModal from '../components/modal/LegacySurveyQuestionModal';
import { FiArrowLeft } from 'react-icons/fi';
import fitSurvey2Services from '../../../../services/fit-survey2.services';

dayjs.locale('th');

const pageSize = 10;

const LegacySurvey2QuestionPage = () => {
  const [legacySurveyQuestionFormOpen, setLegacySurveyQuestionFormOpen] =
    useState(false);
  const {
    data: legacySurveyQuestionLists,
    setData,
    page,
    setPage,
    total,
    setTotal,
  } = usePaginationHook();
  const queryClient = useQueryClient();
  const history = useHistory();
  const params = useParams();

  const legacySurveyQuestionForm = useForm({
    defaultValues: legacySurveyQuestionFormDefaultValues,
    resolver: legacySurveyQuestionFormSchema(),
    mode: 'onChange',
  });

  const _HandleLegacySurveyQuestionFormOpen = (formType) => {
    legacySurveyQuestionForm.setValue('formType', formType);
    setLegacySurveyQuestionFormOpen(true);
  };

  const _HandleLegacySurveyQuestionFormClose = () => {
    legacySurveyQuestionForm.reset(legacySurveyQuestionFormDefaultValues);
    setLegacySurveyQuestionFormOpen(false);
  };

  const { isFetching: legacySurveyQuestionListsLoading } =
    fitSurvey2Services.useQueryGetLegacySurveyQuestionLists(
      { take: pageSize, page, partId: params.partId },
      (data) => {
        setData(data.data);
        setTotal(data.meta.total);
      },
    );

  const { mutate: deleteLegacySurveyQuestion } =
    fitSurvey2Services.useMutationDeleteLegacySurveyQuestion(
      () => {
        queryClient.invalidateQueries({
          queryKey: ['get-legacy-survey2-question'],
          refetchType: 'all',
        });
      },
      (error) => {
        AlertError({
          title: 'ไม่สามารถดำเนินการได้',
          text: error,
        });
      },
    );

  const { mutate: updateLegacySurveyQuestionDisplay } =
    fitSurvey2Services.useMutationUpdateLegacySurveyQuestionDisplay(() => {
      queryClient.invalidateQueries({
        queryKey: ['get-legacy-survey2-question'],
        refetchType: 'all',
      });
    });

  const { mutate: updateLegacySurveyQuestionIndex } =
    fitSurvey2Services.useMutationUpdateLegacySurveyQuestionIndex(
      () => {
        AlertSuccess('อัพเดทสำเร็จ');
        queryClient.invalidateQueries({
          queryKey: ['get-legacy-survey2-question'],
          refetchType: 'all',
        });
      },
      (error) => {
        AlertError({
          title: 'ไม่สามารถดำเนินการได้',
          text: error,
        });
      },
    );

  const inputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [loading2, setLoading2] = useState(false);

  const { mutate: uploadLegacySurvey2QuestionExcel } =
    fitSurvey2Services.useMutationUploadLegacySurvey2QuestionExcel(
      (data) => {
        setTimeout(() => {
          inputRef.current.value = null;

          queryClient.invalidateQueries({
            queryKey: ['get-legacy-survey2-question'],
            refetchType: 'all',
          });

          setLoading(false);
        }, 600);
      },
      (error) => {
        if (inputRef.current) {
          inputRef.current.value = null;
        }
        setLoading(false);
        AlertError({
          title: 'ไม่สามารถอัพโหลดได้',
          text: error,
        });
      },
    );

  const _HandleUploadLegacySurvey2QuestionExcel = (data) => {
    setLoading(true);
    uploadLegacySurvey2QuestionExcel({
      data,
      partId: params.partId,
    });
  };

  const { mutate: downloadLegacySurvey2QuestionExcel } =
    fitSurvey2Services.useMutationDownloadLegacySurvey2QuestionExcel(() => {
      setTimeout(() => {
        setLoading2(false);
      }, 600);
    });

  const columns = [
    {
      title: 'ลำดับ',
      dataIndex: 'index',
      key: 'index',
      render: (_, record) => (
        <div
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <Select
            value={record.index}
            style={{ width: 80 }}
            onChange={(value) => {
              updateLegacySurveyQuestionIndex({
                id: record.id,
                index: value,
              });
            }}
          >
            {Array.from({ length: total }).map((item, index) => {
              return (
                <Select.Option key={String(index + 1)} value={index + 1}>
                  {index + 1}
                </Select.Option>
              );
            })}
          </Select>
        </div>
      ),
      width: 100,
    },
    {
      title: 'คำถาม',
      dataIndex: 'question',
      key: 'question',
      render: (_, record) => <span>{record.question}</span>,
      width: 300,
    },
    {
      title: 'ประเภท',
      dataIndex: 'type',
      key: 'type',
      render: (_, record) => (
        <span>
          {record.type === 'select' && 'ตัวเลือกแบบเลือกคำตอบเดียว'}
          {record.type === 'text' && 'บรรยายคำตอบ'}
          {record.type === 'multi_select' && 'ตัวเลือกแบบเลือกหลายคำตอบ'}
        </span>
      ),
    },
    {
      title: 'ประเภทคำตอบ',
      dataIndex: 'answerType',
      key: 'answerType',
      render: (_, record) => (
        <span>
          {record.answerType === 'weight' && 'ระดับคะแนน'}
          {record.answerType === 'text' && 'ข้อความ'}
        </span>
      ),
    },
    {
      title: 'คำตอบอื่นๆโปรดระบุ',
      dataIndex: 'isHasOther',
      key: 'isHasOther',
      render: (_, record) => <span>{record.isHasOther ? 'มี' : 'ไม่มี'}</span>,
    },
    {
      title: 'มีช่องแสดงความคิดเห็น',
      dataIndex: 'isUserAnswerText',
      key: 'isUserAnswerText',
      render: (_, record) => (
        <span>{record.isUserAnswerText ? 'มี' : 'ไม่มี'}</span>
      ),
    },
    {
      title: 'บังคับแสดงความคิดเห็น',
      dataIndex: 'required',
      key: 'required',
      render: (_, record) => (
        <span>{record.required ? 'บังคับ' : 'ไม่บังคับ'}</span>
      ),
    },
    {
      title: 'การมองเห็น',
      dataIndex: 'display',
      key: 'display',
      render: (_, record) => <span>{record.display ? 'เปิด' : 'ปิด'}</span>,
    },
    {
      title: 'อัปเดตเมื่อ',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (_, record) => (
        <span>{dayjs(record.updatedAt).format('DD/MMM/YYYY')}</span>
      ),
      sorter: (a, b) =>
        dayjs(a.updatedAt).diff(dayjs(b.updatedAt), 'D') > 0 ? 1 : -1,
    },
    {
      title: '',
      key: 'action',
      render: (_, record) => (
        <div
          style={{ display: 'flex', justifyContent: 'center' }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {record.display ? (
            <div
              style={{ cursor: 'pointer', marginLeft: 10 }}
              onClick={async () => {
                const confirm = await AlertConfirm('ปิดการแสดงหมวดหมู่คำถาม');
                if (confirm)
                  updateLegacySurveyQuestionDisplay({
                    id: record.id,
                    display: false,
                  });
              }}
            >
              <FaEye />
            </div>
          ) : (
            <div
              style={{ cursor: 'pointer', marginLeft: 10 }}
              onClick={async () => {
                const confirm = await AlertConfirm('เปิดการแสดงหมวดหมู่คำถาม');
                if (confirm)
                  updateLegacySurveyQuestionDisplay({
                    id: record.id,
                    display: true,
                  });
              }}
            >
              <FaEyeSlash />
            </div>
          )}
          <div
            style={{ cursor: 'pointer', marginLeft: 10 }}
            onClick={() => {
              legacySurveyQuestionForm.setValue('id', record.id);
              _HandleLegacySurveyQuestionFormOpen('edit');
            }}
          >
            <FaPen />
          </div>
          <div
            style={{ cursor: 'pointer', marginLeft: 10 }}
            onClick={async () => {
              const confirm = await AlertConfirm('ลบข้อมูล');
              if (confirm) deleteLegacySurveyQuestion({ id: record.id });
            }}
          >
            <MdDelete />
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <h3
          onClick={() => {
            history.goBack();
          }}
          className="mr-2"
          style={{ cursor: 'pointer' }}
        >
          <FiArrowLeft />
        </h3>
      </div>
      <Panel>
        <PanelHeader>คำถาม</PanelHeader>
        <PanelBody>
          <div
            style={{ display: 'flex', alignItems: 'center' }}
            className="mb-2"
          >
            <div style={{ flex: 1 }} />
            <input
              type="file"
              id={'patient'}
              onChange={(e) => {
                if (e.target.files?.length > 0) {
                  const formData = new FormData();
                  formData.append('file', e.target.files[0]);
                  _HandleUploadLegacySurvey2QuestionExcel(formData);
                }
              }}
              style={{ display: 'none' }}
              ref={inputRef}
            />
            <Button
              className="mr-2"
              onClick={() => {
                if (inputRef.current) {
                  inputRef.current.click();
                }
              }}
              loading={loading}
              type="primary"
            >
              <FaUpload className="mr-2" />
              อัพโหลด excel
            </Button>
            <Button
              ghost
              onClick={() => {
                setLoading2(true);
                downloadLegacySurvey2QuestionExcel({
                  partId: params.partId,
                });
              }}
              loading={loading2}
              type="primary"
              className="mr-2"
            >
              <FaDownload className="mr-2" />
              โหลด excel
            </Button>
            <Button
              onClick={() => {
                _HandleLegacySurveyQuestionFormOpen('create');
              }}
              type="primary"
            >
              <FaPlus className="mr-2" />
              เพิ่มคำถาม
            </Button>
          </div>
          <Table
            columns={columns}
            dataSource={legacySurveyQuestionLists}
            loading={legacySurveyQuestionListsLoading}
            style={{ backgroundColor: '#fff' }}
            rowKey="id"
            scroll={{ x: 'max-content' }}
            onChange={({ current }) => {
              setPage(current);
            }}
            pagination={{
              pageSize: 10,
              total,
            }}
            onRow={(record, rowIndex) => {
              return {
                onClick: (event) => {
                  history.push(
                    `/questionaire/survey2/${params.surveyId}/part/${params.partId}/${record.type}/${record.id}`,
                  );
                },
              };
            }}
          />
        </PanelBody>
        <FormProvider {...legacySurveyQuestionForm}>
          <LegacySurveyQuestionModal
            open={legacySurveyQuestionFormOpen}
            handleClose={_HandleLegacySurveyQuestionFormClose}
          />
        </FormProvider>
      </Panel>
    </>
  );
};

export default LegacySurvey2QuestionPage;
